import { Tooltip, Box } from "@mui/material";
import { useTheme } from "@mui/material";
import React, { JSX } from "react";
import ISONumber from "components/formatting/ISONumber";
import ISOPercentage from "components/formatting/ISOPercentage";
import SubqueriesPortionTooltip from "../Tooltips/SubqueriesPortionTooltip";
import { QueryDrilldownDialog } from "components/dialogs/QueryDrilldown/QueryDrilldownDialog";
import { useTranslation } from "react-i18next";

export interface SubqueriesVolumeProps {
  rowResponse: any;
  rootResponse: any;
  widgetSettings?: any;
}

export default function SubqueriesVolume(props: SubqueriesVolumeProps): JSX.Element {
  const theme = useTheme();

  const { rowResponse, rootResponse } = props;
  const { i18n } = useTranslation();

  const activeQueryIDs = rowResponse?.originatingRequest?.activeQueryIds;

  const [portionIsHovered, setPortionIsHovered] = React.useState<boolean>(false);
  const [portionDrilldownIsOpen, setPortionDrilldownIsOpen] = React.useState<boolean>(false);

  const rowBody = rowResponse.responseBody;
  const rootBody = rootResponse.responseBody;

  const volumeHistogram = rowBody?.histogram?._buckets || [];

  const volumeField = props.widgetSettings?.volumeField === "social_universe" ? "social_universe" : "_count";

  const volumeDatapoints = volumeHistogram?.map((bucket: any) => bucket[volumeField] || 0) || [];
  const sumVolume = volumeDatapoints.reduce((acc: number, val: number) => acc + val, 0);

  const rootVolumeHistogram = rootBody?.histogram?._buckets || [];
  const rootVolumeDatapoints = rootVolumeHistogram?.map((bucket: any) => bucket[volumeField] || 0) || [];
  const sumRootVolume = rootVolumeDatapoints.reduce((acc: number, val: number) => acc + val, 0);

  const rowPercent = sumRootVolume > 0 ? sumVolume / sumRootVolume : 0;

  const volumeWidth = `${(rowPercent * 100).toFixed(2)}%`;

  return (
    <Tooltip title={
        <SubqueriesPortionTooltip
          rowVolume={sumVolume}
          rootVolume={sumRootVolume}
          volumeField={props.widgetSettings?.volumeField || "records"}
          />
      }
      open={portionIsHovered}
      followCursor={true}>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, height: "100%", maxHeight: "50px", position: "relative", cursor: "pointer" }}
        onMouseEnter={() => {
          setPortionIsHovered(true);
        }}
        onMouseLeave={() => {
          setPortionIsHovered(false);
        }}
        onClick={() => {
          setPortionDrilldownIsOpen(true);
        }}
        >
        <Box>
          <ISONumber typographyVariant="subtitle2" fitToContainer={false} number={sumVolume} sx={{ color: theme.palette.primary.main, fontWeight: "bold" }}/>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <ISOPercentage hideTooltip={true} value={rowPercent} sx={{ fontWeight: "bold", color: theme.palette.primary.main }} typographyVariant={"subtitle2"} />
        </Box>
        <Box sx={{ width: "100%", height: "26px", position: "relative", gridColumn: "1 / span 2" }}>
          <Box sx={{ height: "100%", backgroundColor: theme.palette.grey[300], position: "relative" }} >
            <Box sx={{ width: volumeWidth ?? 0, height: "100%", backgroundColor: theme.palette.primary.main }} />
          </Box>
        </Box>
        {portionDrilldownIsOpen && (
          <QueryDrilldownDialog
            title={ `${i18n.t("widget-types.subqueries.subquery")} ${rowResponse.queryDisplayTitle}`}
            onClose={() => setPortionDrilldownIsOpen(false)}
            activeQueryIds={activeQueryIDs}
          />
        )}
      </Box>
    </Tooltip>
  );
}